import React, { memo } from 'react';
import { useTheme } from '@mui/material';
import { useThemeSettings } from '@webbyx/mui';
import { ApexOptions } from 'apexcharts';

import { ChartProps } from '../props';
import ReactApexcharts from '../react-apexchart';

export const BarChart = memo(function BarChart(props: ChartProps) {
  const { colors, data, defaultMaximum = '10k' } = props;
  const theme = useTheme();
  const { settings } = useThemeSettings();

  const options: ApexOptions = {
    chart: {
      offsetX: -10,
      stacked: false,
      parentHeightOffset: 0,
      toolbar: { show: false },
      background: 'transparent !important',
    },
    theme: {
      mode: settings.mode === 'semi-dark' ? 'light' : settings.mode,
    },
    fill: { opacity: 1 },
    dataLabels: { enabled: false },
    colors: colors,
    title: {
      text: data?.title,
      style: {
        color: theme.palette.text.secondary,
      },
    },
    legend: {
      show: false,
    },
    stroke: {
      show: true,
      colors: ['transparent !important'],
    },
    plotOptions: {
      bar: {
        columnWidth: '24px',
        borderRadius: 12,
        borderRadiusApplication: 'end',
      },
    },
    grid: {
      borderColor: '#EC6C0C1A',
      xaxis: {
        lines: { show: false },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    yaxis: {
      tickAmount: 4,
      labels: {
        show: true,
        style: {
          colors: theme.palette.text.secondary,
          fontWeight: 600,
        },
        formatter(val: number, opts?: any): string | string[] {
          if (val >= 1000) {
            if (val / 1000 === Infinity) return defaultMaximum;
            return val / 1000 + 'k';
          } else {
            return val.toString();
          }
        },
      },
    },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { color: theme.palette.divider },
      categories: data?.labels ?? [],
      crosshairs: {
        stroke: { color: theme.palette.divider },
      },
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontWeight: 600,
        },
      },
    },
    responsive: [
      {
        breakpoint: 576,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '14px',
              borderRadius: 7,
            },
          },
          chart: {
            height: 320,
          },
        },
      },
      {
        breakpoint: 1024,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '20px',
              borderRadius: 10,
            },
          },
          chart: {
            height: 400,
          },
        },
      },
    ],
  };
  return (
    <ReactApexcharts
      type="bar"
      height={400}
      options={options}
      series={data?.series}
    />
  );
});

export default BarChart;
